import React, { useEffect, useMemo, useState } from 'react';
import Table from 'react-bootstrap/Table';
import ActionIcon from '../ui/ActionIcon';
import StatusBadge from '../ui/StatusBadge';
import StatusSelect from '../ui/StatusSelect';
import {
  filterNoActionTakenCandidates,
  getPersistedCandidateStatus,
  getStatusDisplayLabel,
  isNoActionTakenProfile,
} from '../../utils/candidateStatus';
import { formatDisplayDate, formatLwdDisplay } from '../../utils/dateFormat';
import { highlightProfileRow } from '../../utils/profileDeepLink';

const STATUS_FILTER_LEADING_OPTIONS = [
  { value: 'No Action Taken', label: getStatusDisplayLabel('No Action Taken') },
];

const SOURCE_PROFILES_GREEN_STATUSES = new Set([
  'L1 Selected',
  'L2 Selected',
  'L3 Selected',
  'Offer Released',
  'Offer Accepted',
  'Joined',
]);

export function groupCandidatesByRecruiter(scopedCandidates = [], recruitersData = []) {
  const groups = new Map();

  scopedCandidates.forEach((candidate) => {
    const recruiterId = Array.isArray(candidate.recruiterId)
      ? String(candidate.recruiterId[0] || '')
      : String(candidate.recruiterId || '');
    const groupKey = recruiterId || candidate.recruiterName || 'unknown';
    const recruiterFromApi = recruitersData.find((item) => (
      String(item.recruiter?._id || '') === recruiterId
      || item.recruiter?.EmployeeName === candidate.recruiterName
    ));

    if (!groups.has(groupKey)) {
      groups.set(groupKey, {
        recruiter: recruiterFromApi?.recruiter || { EmployeeName: candidate.recruiterName || '—' },
        candidates: [],
      });
    }

    groups.get(groupKey).candidates.push(candidate);
  });

  return [...groups.values()];
}

function SourceProfilesList({
  recruitersData = [],
  initialStatusFilter = '',
  noActionFilter = false,
  scopedCandidates = null,
  onViewCandidate,
  highlightProfileId = '',
  resetKey = '',
  showClientColumn = false,
  hideNameSearch = false,
}) {
  const [candidateName, setCandidateName] = useState('');
  const [selectedCandidateStatus, setSelectedCandidateStatus] = useState('');

  useEffect(() => {
    setCandidateName('');
    setSelectedCandidateStatus(noActionFilter ? '' : (initialStatusFilter || ''));
  }, [resetKey, initialStatusFilter, noActionFilter]);

  const sourceRecruitersData = useMemo(() => {
    if (Array.isArray(scopedCandidates)) {
      return groupCandidatesByRecruiter(scopedCandidates, recruitersData);
    }
    return recruitersData;
  }, [scopedCandidates, recruitersData]);

  const filteredRecruitersData = useMemo(() => (
    sourceRecruitersData
      .map((item) => ({
        ...item,
        candidates: (item.candidates || []).filter((candidate) => {
          if (noActionFilter && !isNoActionTakenProfile(candidate)) {
            return false;
          }

          const recentStatus = getPersistedCandidateStatus(candidate);
          const matchesStatus = selectedCandidateStatus
            ? recentStatus === selectedCandidateStatus
            : true;
          const fullName = `${candidate.firstName || ''} ${candidate.lastName || ''}`.trim();
          const matchesName = candidateName
            ? fullName.toLowerCase().includes(candidateName.toLowerCase())
            : true;
          return matchesStatus && matchesName;
        }),
      }))
      .filter((item) => item.candidates.length > 0)
  ), [sourceRecruitersData, selectedCandidateStatus, candidateName, noActionFilter]);

  useEffect(() => {
    if (!highlightProfileId) return undefined;
    return highlightProfileRow(highlightProfileId, { delayMs: 350 });
  }, [highlightProfileId, filteredRecruitersData]);

  const totalProfiles = filteredRecruitersData.reduce(
    (sum, item) => sum + item.candidates.length,
    0,
  );

  const scopedCount = Array.isArray(scopedCandidates) ? scopedCandidates.length : null;
  const expectedCount = scopedCount ?? (noActionFilter
    ? filterNoActionTakenCandidates(
      sourceRecruitersData.flatMap((item) => item.candidates || []),
    ).length
    : null);

  return (
    <>
      <div className="hr-requirement-profiles-toolbar">
        {!hideNameSearch && (
          <input
            type="search"
            className="form-control"
            placeholder="Search by source profile name"
            value={candidateName}
            onChange={(e) => setCandidateName(e.target.value)}
          />
        )}
        <StatusSelect
          className="hr-requirement-profiles-status-filter"
          value={selectedCandidateStatus}
          onChange={(e) => setSelectedCandidateStatus(e.target.value)}
          placeholder="All Statuses"
          leadingOptions={STATUS_FILTER_LEADING_OPTIONS}
        />
      </div>

      <p className="hr-page-hint mb-3">
        {totalProfiles} profile{totalProfiles === 1 ? '' : 's'} shown
        {noActionFilter ? ' with no action for 2+ days' : ''}
        {selectedCandidateStatus ? ` for status “${getStatusDisplayLabel(selectedCandidateStatus)}”` : ''}
        {expectedCount != null && !selectedCandidateStatus && !candidateName
          ? ` (${expectedCount} total)`
          : ''}.
      </p>

      {totalProfiles === 0 ? (
        <p className="hr-empty-state">No source profiles match the current filters.</p>
      ) : (
        <div className="table-responsive hr-requirement-profiles-table">
          <Table bordered hover responsive className="hr-table table-sm">
            <thead>
              <tr>
                <th>Employee Name</th>
                <th>Source Profile Name</th>
                {showClientColumn ? <th>Client</th> : null}
                <th>Role</th>
                <th>ECTC</th>
                <th>LWD</th>
                <th>Uploaded Date</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredRecruitersData.map((item, i) => (
                item.candidates.map((candidate, index) => (
                  <tr
                    key={`${i}-${candidate._id || index}`}
                    id={candidate._id ? `profile-row-${candidate._id}` : undefined}
                  >
                    {index === 0 && (
                      <td rowSpan={item.candidates.length}>
                        {item.recruiter?.EmployeeName || '—'}
                      </td>
                    )}
                    <td>{candidate.firstName} {candidate.lastName}</td>
                    {showClientColumn ? (
                      <td>{candidate.client && String(candidate.client).trim() ? candidate.client : '—'}</td>
                    ) : null}
                    <td>{candidate.role || '—'}</td>
                    <td>{candidate.ectc || '—'}</td>
                    <td>
                      {formatLwdDisplay(candidate.lwd)}
                    </td>
                    <td>{formatDisplayDate(candidate.uploadedOn)}</td>
                    <td>
                      {(() => {
                        const statusRaw = getPersistedCandidateStatus(candidate);
                        const statusLabel = statusRaw || 'No Action Taken';
                        if (SOURCE_PROFILES_GREEN_STATUSES.has(statusRaw)) {
                          return (
                            <span className="hr-source-status hr-source-status-green">
                              {statusLabel}
                            </span>
                          );
                        }
                        return (
                          <StatusBadge status={statusRaw} useStoredLabel />
                        );
                      })()}
                    </td>
                    <td>
                      <ActionIcon
                        variant="view"
                        src="/Images/view.svg"
                        alt="View candidate"
                        onClick={() => onViewCandidate?.(candidate._id)}
                      />
                    </td>
                  </tr>
                ))
              ))}
            </tbody>
          </Table>
        </div>
      )}
    </>
  );
}

export default SourceProfilesList;
